import { List, ListItemButton, ListItemIcon, ListItemText, Stack } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import { navigationConfig, isMenuItemActive, MenuItem } from '../../config/navigation.config';

const colors = {
  primary: '#003ec7',
  onPrimary: '#ffffff',
  onSurface: '#0b1c30',
  onSurfaceVariant: '#434656',
  outline: '#737688',
  outlineVariant: '#c3c5d9',
  surfaceContainer: '#e5eeff',
  surfaceContainerHigh: '#dce9ff',
  surfaceContainerLowest: '#ffffff',
};

export interface MenuContentProps {
  /**
   * Callback when a menu item is clicked
   * Used to close mobile drawer after navigation
   */
  onItemClick?: () => void;

  /**
   * Callback for logout action
   * Required to handle the special logout menu item
   */
  onLogout: () => void;
}

/**
 * Menu Content Component
 *
 * Renders navigation menu items from the navigation configuration.
 * Design aligned with Stitch: Nexus Admin design system.
 *
 * Features:
 * - Active item: left blue border + light blue background + bold text
 * - Hover: subtle background change
 * - Icon + label layout
 */
export function MenuContent({ onItemClick, onLogout }: MenuContentProps) {
  const location = useLocation();
  const navigate = useNavigate();

  /**
   * Handle menu item click
   * Navigates to the item's path or executes its action
   */
  const handleItemClick = (item: MenuItem) => {
    // Handle special actions
    if (item.action === 'logout') {
      onLogout();
      onItemClick?.();
      return;
    }

    // Handle navigation
    if (item.path) {
      navigate(item.path);
      onItemClick?.();
    }
  };

  /**
   * Render a single menu item
   */
  const renderMenuItem = (item: MenuItem) => {
    const Icon = item.icon;
    const isActive = item.path ? isMenuItemActive(item.path, location.pathname) : false;

    return (
      <ListItemButton
        key={item.id}
        selected={isActive}
        onClick={() => handleItemClick(item)}
        aria-label={item.label}
        aria-current={isActive ? 'page' : undefined}
        sx={{
          mb: '4px',
          borderRadius: '8px',
          px: 1,
          py: 0.75,
          color: isActive ? colors.primary : colors.onSurfaceVariant,
          backgroundColor: isActive ? colors.surfaceContainerHigh : 'transparent',
          borderLeft: isActive ? `4px solid ${colors.primary}` : '4px solid transparent',
          transition: 'background-color 0.2s, color 0.2s',
          '&:hover': {
            backgroundColor: isActive ? colors.surfaceContainerHigh : colors.surfaceContainer,
            color: isActive ? colors.primary : colors.onSurface,
          },
          '&.Mui-selected': {
            backgroundColor: colors.surfaceContainerHigh,
            color: colors.primary,
            '&:hover': {
              backgroundColor: colors.surfaceContainerHigh,
            },
          },
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: 36,
            color: 'inherit',
          }}
        >
          <Icon sx={{ fontSize: 20 }} />
        </ListItemIcon>
        <ListItemText
          primary={item.label}
          primaryTypographyProps={{
            fontSize: '13px',
            fontFamily: 'Inter, sans-serif',
            fontWeight: isActive ? 700 : 600,
            letterSpacing: '0.05em',
            color: 'inherit',
          }}
        />
      </ListItemButton>
    );
  };

  return (
    <Stack sx={{ flexGrow: 1, justifyContent: 'space-between', overflowY: 'auto' }}>
      {/* Primary Navigation */}
      <List dense sx={{ p: 0 }}>
        {navigationConfig.primary.map((item) => renderMenuItem(item))}
      </List>

      {/* Secondary Navigation (Settings & Logout) */}
      <List
        dense
        sx={{
          p: 0,
          mt: 'auto',
          borderTop: `1px solid ${colors.outlineVariant}`,
          pt: 1,
        }}
      >
        {navigationConfig.secondary.map((item) => renderMenuItem(item))}
      </List>
    </Stack>
  );
}
